import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Input } from '../../components/ui/input'
import { Button } from '../../components/ui/button'
import { toast as message } from 'sonner'
import { sendCode, resetPassword } from '../../services/auth'
import EmailCodeVerification from '../../components/common/EmailCodeVerification'
import { useEmailCodeVerification } from '../../hooks/useEmailCodeVerification'

interface ResetPasswordForm {
  email: string
  code: string
  newPassword: string
  confirmPassword: string
}

export default function ResetPassword() {
  const [loading, setLoading] = useState(false)
  const [sendingCode, setSendingCode] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [success, setSuccess] = useState(false)
  const [values, setValues] = useState<ResetPasswordForm>({
    email: '',
    code: '',
    newPassword: '',
    confirmPassword: '',
  })
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown((prev) => prev - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])
  const navigate = useNavigate()
  const email = values.email
  const emailVerification = useEmailCodeVerification(email?.trim() ?? '')

  const handleSendCode = async () => {
    try {
      const email = values.email
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        message.error('請輸入有效的郵箱地址')
        return
      }
      setSendingCode(true)
      await emailVerification.send((token) => sendCode(email, 'reset', token))
      message.success('驗證碼已發送。如果沒有收到，請先檢查垃圾郵箱。')
      setCountdown(60)
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '發送驗證碼失敗')
    } finally {
      setSendingCode(false)
    }
  }

  const handleSubmit = async (values: ResetPasswordForm) => {
    if (!emailVerification.ready) {
      message.warning('請先完成人機驗證')
      return
    }
    if (values.newPassword !== values.confirmPassword) {
      message.error('兩次輸入的密碼不一致')
      return
    }
    setLoading(true)
    try {
      await resetPassword(values.email, values.code, values.newPassword)
      setSuccess(true)
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '重置密碼失敗')
    } finally {
      setLoading(false)
    }
  }

  if (success)
    return (
      <main className="flex min-h-screen items-center justify-center bg-muted/30 p-5">
        <section className="w-full max-w-md rounded-2xl border bg-white p-8 text-center">
          <h1 className="text-xl font-semibold">密碼重置成功</h1>
          <p className="my-5 text-muted-foreground">
            您的密碼已成功重置，請使用新密碼登入。
          </p>
          <Button onClick={() => navigate('/login')}>返回登入</Button>
        </section>
      </main>
    )
  return (
    <main className="flex min-h-screen items-center justify-center bg-muted/30 p-5">
      <section className="w-full max-w-md rounded-2xl border bg-white p-8">
        <h1 className="text-2xl font-semibold">重置密碼</h1>
        <p className="mb-7 mt-2 text-sm text-muted-foreground">
          輸入您的郵箱以重置密碼
        </p>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault()
            void handleSubmit(values)
          }}
        >
          <label className="block text-sm">
            郵箱
            <Input
              className="mt-2"
              type="email"
              aria-label="郵箱"
              placeholder="郵箱"
              required
              value={values.email}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, email: event.target.value }))
              }
            />
          </label>
          <label className="block text-sm">
            驗證碼
            <div className="mt-2 flex gap-2">
              <Input
                aria-label="驗證碼"
                placeholder="驗證碼"
                required
                value={values.code}
                onChange={(event) =>
                  setValues((prev) => ({ ...prev, code: event.target.value }))
                }
              />
              <Button
                type="button"
                variant="outline"
                disabled={
                  sendingCode || countdown > 0 || !emailVerification.canSend
                }
                onClick={handleSendCode}
              >
                {countdown > 0
                  ? `${countdown}s`
                  : sendingCode
                    ? '發送中…'
                    : '獲取驗證碼'}
              </Button>
            </div>
          </label>
          <label className="block text-sm">
            新密碼
            <Input
              className="mt-2"
              type="password"
              aria-label="新密碼"
              placeholder="新密碼（至少8位）"
              required
              minLength={8}
              value={values.newPassword}
              onChange={(event) =>
                setValues((prev) => ({
                  ...prev,
                  newPassword: event.target.value,
                }))
              }
            />
          </label>
          <label className="block text-sm">
            確認新密碼
            <Input
              className="mt-2"
              type="password"
              aria-label="確認新密碼"
              placeholder="確認新密碼"
              required
              value={values.confirmPassword}
              onChange={(event) =>
                setValues((prev) => ({
                  ...prev,
                  confirmPassword: event.target.value,
                }))
              }
            />
          </label>
          <EmailCodeVerification verification={emailVerification} />
          <Button
            type="submit"
            disabled={loading || !emailVerification.ready}
            className="w-full"
          >
            {loading ? '重置中…' : '重置密碼'}
          </Button>
        </form>
        <div className="mt-5 text-center text-sm text-primary">
          <Link to="/login">返回登入</Link>
        </div>
      </section>
    </main>
  )
}
